import { useState } from "react";
import instance from "../Api/Instance";
import { back } from "../Api/ApiUrl";
import { Modal } from "antd";
import { togglePaginating } from "../utils/setterUtils";

const getError = (error, actionType) => {
  const actionValues = {
    update: "actualizar",
    delete: "borrar",
  };

  if (error?.response && error?.response?.data) {
    return error.response.data;
  }

  return {
    apierror: {
      status: "ERROR",
      message: `Error al ${actionValues?.[actionType]} destinatario.`,
    },
  };
};

const buildRecipientsSuccessState = (
  prev,
  { page, size, participantName, participantAlias, response }
) => ({
  ...prev,
  isLoading: false,
  isPaginating: false,
  page,
  size,
  participantName,
  participantAlias,
  total: response?.data?.totalElements,
  data: response?.data?.content,
  errors: [],
  searchWasMade: true,
});

const buildRecipientsEmptyState = (
  prev,
  { page, size, participantName, participantAlias }
) => ({
  ...prev,
  isLoading: false,
  isPaginating: false,
  page,
  size,
  participantName,
  participantAlias,
  total: 0,
  data: [],
  searchWasMade: true,
});

const buildRecipientsErrorState = ({
  page,
  size,
  participantName,
  participantAlias,
  error,
}) => ({
  isLoading: false,
  isPaginating: false,
  page,
  size,
  participantName,
  participantAlias,
  total: 0,
  data: [],
  error,
  searchWasMade: true,
});

const handleRecipientCreateResponse = (response, mustRefetch, refetchFn) => {
  if (response.status === 201) {
    if (mustRefetch && typeof refetchFn === "function") {
      refetchFn();
    }
    return { status: "ok", data: response?.data };
  }
  return {
    status: "error",
    error: "Se ha producido un error al guardar el destinatario.",
  };
};

const removeFromRecipients = (list, recipientId) =>
  list.filter((item) => item.participantId !== recipientId);

export default function useRecipients() {
  const [recipientsDetails, setRecipientsDetails] = useState({
    isLoading: false,
    isPaginating: false,
    page: 1,
    size: 10,
    total: 0,
    data: [],
    errors: [],
    searchWasMade: false,
  });

  //Primero hay que separar la funcionalidad de getRecipients el cuál obtiene un listado de remitentes para la búsqueda de remitentes en pre-registro
  const getRecipients = async (recipientName, recipientAlias) => {
    try {
      const response = await instance.get(back.getParticipants, {
        params: {
          participantName: recipientName,
          participantAlias: recipientAlias,
          participantType: 2,
        },
      });

      if (response.status === 200) {
        const data = response?.data?.slice(0, 10);

        return data;
      }

      setRecipientsDetails((prevState) => ({
        ...prevState,
        isLoading: false,
        data: [],
      }));

      return [];
    } catch (error) {
      console.error("Error al solicitar destinatarios => " + error?.message);
    }
  };

  //Y aquí la funcionalidad de getPagedRecipients el cuál obtiene un listado de remitentes paginado para la propia página de remitentes
  const getPagedRecipients = async (
    page = 1,
    size = 10,
    participantName,
    participantAlias,
    paginating = false
  ) => {
    try {
      setRecipientsDetails((prev) => ({
        ...prev,
        isLoading: !paginating,
        isPaginating: paginating,
        page,
        size,
        participantName,
        participantAlias,
        data: paginating ? prev.data : [],
        errors: [],
        searchWasMade: paginating ? true : false,
      }));

      const response = await instance.get(back.getPagedParticipants, {
        params: {
          page: page - 1,
          participantName,
          participantAlias,
          size,
          participantType: 2,
        },
      });

      if (response.status === 200) {
        setRecipientsDetails((prev) =>
          buildRecipientsSuccessState(prev, {
            page,
            size,
            participantName,
            participantAlias,
            response,
          })
        );
        return response?.data?.content;
      }

      setRecipientsDetails((prev) =>
        buildRecipientsEmptyState(prev, {
          page,
          size,
          participantName,
          participantAlias,
        })
      );
      return [];
    } catch (error) {
      console.error(error);
      setRecipientsDetails(
        buildRecipientsErrorState({
          page,
          size,
          participantName,
          participantAlias,
          error,
        })
      );
      throw new Error(`Al solicitar destinatarios => ${error?.message}`);
    }
  };

  const refetchCurrentRecipients = () =>
    getPagedRecipients(
      recipientsDetails.page,
      recipientsDetails.size,
      recipientsDetails.participantName,
      recipientsDetails.participantAlias,
      true
    );

  const addRecipient = async (recipientDetails, mustRefetch = true) => {
    try {
      const response = await instance.post(
        back.addParticipant,
        recipientDetails,
        { params: { participantType: 2 } }
      );

      return handleRecipientCreateResponse(
        response,
        mustRefetch,
        refetchCurrentRecipients
      );
    } catch (error) {
      return {
        status: "error",
        error: `Se ha producido el siguiente error al guardar el destinatario: ${error?.message} `,
      };
    }
  };

  const addParticipantByFile = async (recipientDetails, mustRefetch = true) => {
    const { file, fileBase64 } = recipientDetails;

    if (!file || !fileBase64) {
      return Modal.error({
        title: "Error al añadir restricciones",
        content: "No se ha seleccionado ningún archivo.",
      });
    }

    try {
      const response = await instance.post(
        back.addParticipantsByFile,
        { file: fileBase64 },
        { params: { participantType: 2 } }
      );

      return handleRecipientCreateResponse(
        response,
        mustRefetch,
        refetchCurrentRecipients
      );
    } catch (error) {
      return {
        status: "error",
        error: `Se ha producido el siguiente error al guardar el destinatario: ${error?.message} `,
      };
    }
  };

  const updateRecipient = async (updatedRecipientDetails) => {
    togglePaginating(setRecipientsDetails, true);
    try {
      const response = await instance.put(
        `${back.updateParticipant}/${updatedRecipientDetails?.participantId}`,
        updatedRecipientDetails
      );

      if (response.status === 204) {
        setRecipientsDetails((prevState) => ({
          ...prevState,
          isPaginating: false,
          data: prevState.data.map((item) =>
            item.participantId === updatedRecipientDetails.participantId
              ? { ...item, ...updatedRecipientDetails }
              : item
          ),
        }));
        togglePaginating(setRecipientsDetails, false);
        return true;
      }

      togglePaginating(setRecipientsDetails, false);

      return false;
    } catch (error) {
      console.error(error);

      togglePaginating(setRecipientsDetails, false);
      getError(error, "update");
    }
  };

  const deleteRecipient = async (recipientId) => {
    togglePaginating(setRecipientsDetails, true);
    try {
      const response = await instance.delete(
        `${back.deleteParticipant}/${recipientId}`
      );

      if (response.status === 204) {
        setRecipientsDetails((prevState) => ({
          ...prevState,
          isPaginating: false,
          data: removeFromRecipients(prevState.data, recipientId),
        }));
        return true;
      }
      togglePaginating(setRecipientsDetails, false);
      return false;
    } catch (error) {
      togglePaginating(setRecipientsDetails, false);
      console.error(error);
      getError(error, "delete");
    }
  };

  return {
    setRecipientsDetails,
    recipientsDetails,
    addRecipient,
    addParticipantByFile,
    getRecipients,
    getPagedRecipients,
    updateRecipient,
    deleteRecipient,
  };
}
